<template>
  <div class="flex flex-col h-full overflow-hidden">
    <!--     Add Customer     -->
    <div v-if="!showAddNewCustomer" @click="openAddCustomer"
         class="text-gray-900 font-medium px-2 cursor-pointer text-center flex items-center justify-center gap-2 h-16 border-b flex-shrink-0">
      <i class="pi pi-plus" />
      <span>יצירת לקוח חדש</span>
    </div>
    <div v-else class="flex flex-col gap-2 py-4 border-b p-4 overflow-x-hidden flex-shrink-0">
      <span class="font-medium">יצירת לקוח חדש</span>
      <div class="flex gap-2 w-full">
        <div class="flex flex-col gap-1 w-1/2 min-w-0">
          <label class="text-sm font-medium" for="fullName">שם מלא</label>
          <InputText
            id="fullName"
            size="small"
            v-model="newCustomer.fullName"
            placeholder="שם מלא"
            :invalid="submitted && !newCustomer.fullName"
          />
          <small v-if="submitted && !newCustomer.fullName" class="text-red-500">
            שם מלא הוא שדה חובה
          </small>
        </div>
        <div class="flex flex-col gap-1 w-1/2 min-w-0">
          <label class="text-sm font-medium" for="phoneNumber">מספר טלפון</label>
          <InputText
            id="phoneNumber"
            size="small"
            v-model="formattedPhoneNumber"
            placeholder="מספר טלפון"
            :invalid="submitted && !newCustomer.phoneNumber"
            @input="formatPhoneNumber"
            maxlength="12"
          />
          <small v-if="submitted && !newCustomer.phoneNumber" class="text-red-500">
            מספר טלפון הוא שדה חובה
          </small>
          <small v-if="errorMessage" class="text-red-500">
            {{ errorMessage }}
          </small>
        </div>
      </div>
      <div class="flex gap-1 w-full justify-end">
        <Button label="ביטול" size="small" text @click="closeAddCustomer" />
        <Button label="שמירה" size="small" @click="addNewCustomer" :loading="savingCustomer" />
      </div>
    </div>
    <!--     Search     -->
    <div class="w-full p-2 flex-shrink-0">
      <IconField>
        <InputIcon class="pi pi-search" />
        <InputText size="small" class="w-full" v-model="searchValue" placeholder="חיפוש" />
      </IconField>
    </div>
    <!--     Customer List     -->
    <div class="flex-1 min-h-0 overflow-y-auto overflow-x-hidden">
      <div v-if="loading" class="flex flex-col">
        <div v-for="i in [1,2,3]"
             class="w-full py-4 border-b flex gap-3 justify-start items-center border-gray-200 px-4">
          <Skeleton shape="circle" size="3rem" />
          <div class="flex flex-grow">
            <div class="flex flex-col gap-2 items-start justify-start">
              <Skeleton borderRadius="12px" width="8rem" height="0.75rem" />
              <Skeleton borderRadius="12px" width="8rem" height="0.5rem" />
            </div>
            <RadioButton inputId="cheese" value="Cheese" class="mr-auto" />
          </div>
        </div>
      </div>
      <div v-else-if="customers.length === 0"
           class="h-full flex flex-col items-center justify-center text-center px-6 py-12 gap-2">
        <div class="w-14 h-14 rounded-full bg-gray-100 flex items-center justify-center mb-1">
          <i :class="searchValue ? 'pi pi-search' : 'pi pi-users'" class="text-2xl text-gray-400" />
        </div>
        <span class="font-medium">{{ searchValue ? 'לא נמצאו לקוחות' : 'עדיין אין לקוחות' }}</span>
        <span class="text-sm text-gray-500 max-w-[16rem]">
          {{ searchValue
            ? 'לא מצאנו לקוח שתואם לחיפוש. נסו שם או מספר אחר, או צרו לקוח חדש.'
            : 'צרו את הלקוח הראשון שלכם כדי לבחור אותו כאן.' }}
        </span>
        <Button
          v-if="!showAddNewCustomer"
          label="יצירת לקוח חדש"
          icon="pi pi-plus"
          size="small"
          outlined
          class="!rounded-full mt-2"
          @click="openAddCustomer"
        />
      </div>
      <div v-else class="flex flex-col px-4">
        <TransitionGroup appear name="list" tag="div" class="flex flex-col w-full">
          <div v-for="(customer, index) in customers"
               :key="customer.id"
               :style="{ transitionDelay: `${index * 50}ms` }"
               @click="selectedCustomerId = customer.id"
               :class="['w-full py-4 flex gap-3 justify-start items-center border-gray-200 cursor-pointer', index !== customers.length - 1 ? 'border-b' : '']">
            <Avatar
              :label="getTwoChars(customer.fullName)"
              size="large"
              shape="circle"
              class="bg-[#b798f5] rounded-2xl text-white" />
            <div class="flex flex-grow justify-between items-center">
              <div class="flex flex-col gap-1 items-start justify-start">
                <span class="font-medium">{{ customer.fullName }}</span>
                <span class="text-sm">{{ formatPhone(customer.phoneNumber) }}</span>
              </div>
              <i :class="['pi', customer.id !== selectedCustomerId ? 'pi-circle': 'pi-circle-fill']" />
            </div>
          </div>
        </TransitionGroup>
      </div>
    </div>
    <div class="w-full px-4 flex-shrink-0 shadow-[0_-8px_16px_-4px_rgba(0,0,0,0.05)]">
      <drawer-footer
        approve-label="בחירת לקוח"
        @approve-click="chooseCustomer"
        @cancel-click="emit('cancel')"
      />
    </div>
  </div>
</template>

<script lang="ts" setup>
import { computed, onMounted, ref, watch, watchEffect } from 'vue';
import type { ICustomer } from '@torim-app/types';
import { CustomersApi } from '@app/api/admin/customers-api';
import { getTwoChars } from '@torim-app/utils';
import IconField from 'primevue/iconfield';
import InputIcon from 'primevue/inputicon';
import { useDrawer } from '@torim-app/shared-components';

const props = defineProps<{
  initialCustomerId?: string;
}>();

const emit = defineEmits<{
  customerSelected: [customer: Pick<ICustomer, 'id' | 'fullName' | 'tags' | 'loyalty' | 'smsOptedOut' | 'phoneNumber'>]
  cancel: []
}>();
const { closeDrawer } = useDrawer();

const customers = ref<Pick<ICustomer, 'id' | 'fullName' | 'tags' | 'loyalty' | 'smsOptedOut' | 'phoneNumber'>[]>([]);
const loading = ref(false);
const limit = ref(50);

const formattedPhoneNumber = ref<string>('');
const submitted = ref(false);
const savingCustomer = ref(false);
const searchValue = ref<string>();
const errorMessage = ref('');
const selectedCustomerId = ref<string | undefined>(props.initialCustomerId);
const newCustomer = ref({
  fullName: '',
  phoneNumber: ''
});
const showAddNewCustomer = ref(false);
let filterTimeout = ref<any>();

const selectedCustomer = computed(() => customers.value.find(c => c.id === selectedCustomerId.value));

const chooseCustomer = () => {
  if (selectedCustomer.value) {
    emit('customerSelected', selectedCustomer.value);
    closeDrawer();
  }
};

const formatPhone = (phoneNumber: string) => {
  // Empty when the viewer isn't allowed to see contact details — don't render bare dashes.
  if (!phoneNumber) return '';
  return `${phoneNumber.substring(0, 3)}-${phoneNumber.substring(3, 6)}-${phoneNumber.substring(6, 10)}`;
};

const loadCustomers = async (search?: string) => {
  loading.value = true;
  try {
    const response = await CustomersApi.fetchCustomers({
      limit: limit.value,
      skip: 0,
      search: search || undefined
    });
    customers.value = response.customers;
  } catch (error) {
    console.error('Failed to load customers:', error);
  } finally {
    loading.value = false;
  }
};

const openAddCustomer = () => {
  showAddNewCustomer.value = true;
  submitted.value = false;
  errorMessage.value = '';
  newCustomer.value = { fullName: '', phoneNumber: '' };
  formattedPhoneNumber.value = '';
};

const closeAddCustomer = () => {
  showAddNewCustomer.value = false;
  submitted.value = false;
  errorMessage.value = '';
  newCustomer.value = { fullName: '', phoneNumber: '' };
  formattedPhoneNumber.value = '';
};

const addNewCustomer = async () => {
  submitted.value = true;
  errorMessage.value = '';

  if (!newCustomer.value.fullName || !newCustomer.value.phoneNumber) {
    return;
  }

  savingCustomer.value = true;
  try {
    await CustomersApi.addCustomer([{
      fullName: newCustomer.value.fullName,
      phoneNumber: newCustomer.value.phoneNumber
    }]);

    await loadCustomers(searchValue.value);

    const addedCustomer = customers.value.find(
      c => c.phoneNumber === newCustomer.value.phoneNumber
    );

    // A freshly created client should be selected immediately and the drawer
    // closed, so the owner doesn't have to find and pick him from the list.
    if (addedCustomer) {
      selectedCustomerId.value = addedCustomer.id;
      closeAddCustomer();
      emit('customerSelected', addedCustomer);
      closeDrawer();
      return;
    }

    closeAddCustomer();
  } catch (error: any) {
    if (error === 'customer-panel already exists') {
      errorMessage.value = 'לקוח עם מספר טלפון זה כבר קיים';
    } else {
      errorMessage.value = 'שגיאה ביצירת לקוח חדש';
    }
    console.error('Failed to add customer:', error);
  } finally {
    savingCustomer.value = false;
  }
};

const formatPhoneNumber = (event: Event) => {
  const input = event.target as HTMLInputElement;
  let value = input.value.replace(/\D/g, '');
  value = value.substring(0, 10);

  if (value.length > 6) {
    value = `${value.slice(0, 3)}-${value.slice(3, 6)}-${value.slice(6)}`;
  } else if (value.length > 3) {
    value = `${value.slice(0, 3)}-${value.slice(3)}`;
  }

  formattedPhoneNumber.value = value;
  newCustomer.value.phoneNumber = value.replace(/\D/g, '');
};

watch(() => searchValue.value, (newValue) => {
  if (filterTimeout) clearTimeout(filterTimeout.value);
  filterTimeout.value = setTimeout(async () => {
    await loadCustomers(newValue);
  }, 300);
});

watchEffect(() => {
  if (newCustomer.value.phoneNumber && !formattedPhoneNumber.value) {
    const digits = newCustomer.value.phoneNumber.replace(/\D/g, '');
    if (digits.length > 6) {
      formattedPhoneNumber.value = `${digits.slice(0, 3)}-${digits.slice(3, 6)}-${digits.slice(6)}`;
    } else if (digits.length > 3) {
      formattedPhoneNumber.value = `${digits.slice(0, 3)}-${digits.slice(3)}`;
    } else {
      formattedPhoneNumber.value = digits;
    }
  }
});

onMounted(() => {
  if (customers.value.length === 0) {
    loadCustomers();
  }
});

// Expose for parent use if needed
defineExpose({ showAddNewCustomer });
</script>
